import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";

// styles
import "../project/Project.css";
import useGetFetch from "../../hooks/useGetFetch";
import Loading from "../../components/Loading";
import InputField from "../../formComponents/InputField";
import { projectStore } from "../../store/Store";
import InvoiceDetailFilter from "../vendor-invoices/InvoiceDetailFilter";
import ErrorDisplay from "../../components/ErrorDisplay";
import AdjustmentDetails from "../../invoiceComponents/AdjustmentDetails";
import AddAdjustment from "../account-invoices/AddAdjustment";
import DeleteAdjustment from "../account-invoices/DeleteAdjustment";
import DeliveredDate from "../../components/DeliveredDate";

export default function CustomerInvoiceDetails() {
  // global
  const projectLoads = projectStore((data) => data.projectLoads);
  const projectAssetLoads = projectStore((data) => data.projectAssetLoads);
  const currentProject = projectStore((data) => data.currentProject);

  // local
  const [invoice, setInvoice] = useState(null);
  const [visibleLoads, setVisibleLoads] = useState([]);
  const [loads, setLoads] = useState([]);
  const [canceled, setCanceled] = useState([]);
  const [mismatchValue, setMismatchValue] = useState(2.0);
  const { invId, isAsset: isAssetParam } = useParams();
  const [filter, setFilter] = useState("all");
  const [adjustments, setAdjustments] = useState();
  const [editAdjustmentTrigger, setEditAdjustmentTrigger] = useState(false);
  const [deleteAdjustmentTrigger, setDeleteAdjustmentTrigger] = useState(false);
  const isAsset = isAssetParam === "true"
  const { pending, error } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/customer/invoice/${invId}`,
    setInvoice,
  );
  const { isPending: adjustmentsPending, error: adjustmentsError } =
    useGetFetch(
      `${process.env.REACT_APP_BACKEND}/lc/invoice-adjustment/${invId}?is_vendor=false&is_received=false&project_id=${currentProject.project.id}`,
      setAdjustments,
    );
  useEffect(() => {
    if (invoice && invoice.loads) {
      const canceledLoads = [];
      const regular = [];
      invoice.loads.forEach((l) => {
        if (l.is_canceled) {
          canceledLoads.push(l);
        } else {
          regular.push(l);
        }
      });
      setCanceled(canceledLoads);
      setLoads(regular);
      setVisibleLoads(regular);
    }
  }, [invoice, setCanceled, setLoads, setVisibleLoads]);
  const calcTotals = (invLoad) => {
    var loadTotals = {
      weight: 0,
      weight_diff: 0,
      total_rev: 0,
      calc_rev: 0,
      rev_diff: 0,
      matching_load_numbers: "",
      ticket_is_missing: "",
      locked_reason: "",
      propconnect_approval_status: "",
    };
    var load;
    if (!isAsset) {
      load = projectLoads.filter((x) => x.id === invLoad.load_id);
      if (load.length > 0) {
        loadTotals.weight = load[0].weight_tons;
        loadTotals.weight_diff = invLoad.weight_tons - load[0].weight_tons;
        loadTotals.total_rev =
          invLoad.freight_rev + invLoad.fsc_rev + invLoad.sand_rev;
        loadTotals.calc_rev =
          load[0].freight_rev + load[0].fsc_rev + load[0].sand_rev;
        loadTotals.rev_diff = loadTotals.total_rev - loadTotals.calc_rev;
        if (load[0].matching_load_numbers.length > 1) {
          for (let i = 1; i < load[0].matching_load_numbers.length; i++) {
            loadTotals.matching_load_numbers += `${load[0].matching_load_numbers[i]}`;
          }
        }
        loadTotals.ticket_is_missing = load[0].ticket_is_missing ? "Yes" : "No";
        loadTotals.locked_reason = load[0].locked_reason;
        loadTotals.propconnect_approval_status =
          load[0].propconnect_approval_status;
      }
    } else {
      load = projectAssetLoads.filter((x) => x.id === invLoad.load_id);
      if (load.length > 0) {
        loadTotals.weight = load[0].weight_tons;
        loadTotals.weight_diff = invLoad.weight_tons - load[0].weight_tons;
        loadTotals.total_rev = invLoad.freight_rev + invLoad.fsc_rev;
        loadTotals.calc_rev = load[0].freight_rev + load[0].fsc_rev;
        loadTotals.rev_diff = loadTotals.total_rev - loadTotals.calc_rev;
        loadTotals.locked_reason = load[0].locked_reason
      }
    }
    return loadTotals;
  };

  const handleMismatch = (revDiff) => {
    if (Math.abs(revDiff) > mismatchValue) {
      return "Yes";
    } else {
      return "";
    }
  };

  if (pending || adjustmentsPending) {
    return <Loading />;
  } else if (error || adjustmentsError) {
    return <ErrorDisplay message={"Something Went Wrong"} code={500} />;
  } else if (invoice && projectLoads) {
    return (
      <div className="container-fluid">
        {error && <p className="error">{error}</p>}
        <div className="d-flex justify-content-between project-header">
          <h2>Invoice : {invoice.invoice.invoice_name}</h2>
          <InputField
            name={"mismatch-value"}
            title={"Mismatch Threshold $"}
            value={mismatchValue}
            type={"number"}
            increment={0.01}
            onChange={(e) => setMismatchValue(e.target.value)}
            required
          />
        </div>
        {adjustments &&
          adjustments.invoice_adjustment &&
          adjustments.adjustment_fields && (
            <AdjustmentDetails
              adjustments={adjustments}
              setEditAdjustmentTrigger={setEditAdjustmentTrigger}
              setDeleteAdjustmentTrigger={setDeleteAdjustmentTrigger}
            />
          )}
        {canceled.length !== 0 && (
          <>
            <div className="d-flex justify-content-between project-header">
              <h4>Canceled Loads</h4>
            </div>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Load</th>
                    <th>Delivered</th>
                    <th>Carrier</th>
                    <th>Driver</th>
                    {!isAsset && <th>Ticket</th>}
                    <th>Weight</th>
                    <th>Calc Weight</th>
                    <th>Weight Diff</th>
                    {!isAsset && <th>Product</th>}
                    <th>Freight Rev</th>
                    <th>FSC Rev</th>
                    {!isAsset && <th>Sand Rev</th>}
                    <th>Total Rev</th>
                    <th>Calc Rev</th>
                    <th>Rev Diff</th>
                    <th>Mismatch</th>
                  </tr>
                </thead>
                <tbody>
                  {canceled.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <td>{l.carrier_name}</td>
                      <td>{l.driver_name}</td>
                      {!isAsset && <td>{l.ticket_number}</td>}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      <td>{calcTotals(l).weight.toFixed(2)}</td>
                      <td>{calcTotals(l).weight_diff.toFixed(2)}</td>
                      {!isAsset && <td>{l.product_name}</td>}
                      <td>${l.freight_rev.toFixed(2)}</td>
                      <td>${l.fsc_rev.toFixed(2)}</td>
                      {!isAsset && <td>${l.sand_rev.toFixed(2)}</td>}
                      <td>${calcTotals(l).total_rev.toFixed(2)}</td>
                      <td>${calcTotals(l).calc_rev.toFixed(2)}</td>
                      <td>${calcTotals(l).rev_diff.toFixed(2)}</td>
                      <td>{handleMismatch(calcTotals(l).rev_diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {invoice.uninvoiced_loads && (
          <div>
            <h4>Loads Not Invoiced</h4>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Load</th>
                    <th>Delivered</th>
                    <th>Carrier</th>
                    <th>Driver</th>
                    {!isAsset && <th>Ticket</th>}
                    <th>Weight</th>
                    {!isAsset && <th>Product</th>}
                    <th>Freight Rev</th>
                    <th>FSC Rev</th>
                    {!isAsset && <th>Sand Rev</th>}
                    <th>Total Rev</th>
                    {!isAsset && <th>Matches</th>}
                    {!isAsset && <th>Missing Ticket</th>}
                    <th>Locked</th>
                    <th>Approval Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.uninvoiced_loads.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.carrier_name}</td>
                      <td>{l.driver_name}</td>
                      {!isAsset && <td>{l.ticket_number}</td>}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      {!isAsset && <td>{l.product_name}</td>}
                      <td>${l.freight_rev.toFixed(2)}</td>
                      <td>${l.fsc_rev.toFixed(2)}</td>
                      {!isAsset && <td>${l.sand_rev.toFixed(2)}</td>}
                      <td>${calcTotals(l).total_rev.toFixed(2)}</td>
                      {!isAsset && <td>{calcTotals(l).matching_load_numbers}</td>}
                      {!isAsset && <td>{calcTotals(l).ticket_is_missing}</td>}
                      <td>{calcTotals(l).locked_reason}</td>
                      <td>{calcTotals(l).propconnect_approval_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="d-flex justify-content-between">
          {loads.length !== 0 && <h4>Invoiced Loads</h4>}
        </div>
        {loads.length !== 0 && (
          <div className="row g-2">
            <InvoiceDetailFilter
              isCustomer={true}
              customerFilter={filter}
              setCustomerfilter={setFilter}
            />
          </div>
        )}
        <div className="load-display-list">
          {loads.length !== 0 && (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Load</th>
                  <th>Delivered</th>
                  <th>Carrier</th>
                  <th>Driver</th>
                  {!isAsset && <th>Ticket</th>}
                  <th>Weight</th>
                  <th>Calc Weight</th>
                  <th>Weight Diff</th>
                  {!isAsset && <th>Product</th>}
                  <th>Freight Rev</th>
                  <th>FSC Rev</th>
                  {!isAsset && <th>Sand Rev</th>}
                  <th>Total Rev</th>
                  <th>Calc Rev</th>
                  <th>Rev Diff</th>
                  <th>Mismatch</th>
                </tr>
              </thead>
              <tbody>
                {visibleLoads.map((l, idx) =>
                  filter !== "cost mismatch" ? (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.carrier_name}</td>
                      <td>{l.driver_name}</td>
                      {!isAsset && <td>{l.ticket_number}</td>}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      <td>{calcTotals(l).weight.toFixed(2)}</td>
                      <td>{calcTotals(l).weight_diff.toFixed(2)}</td>
                      {!isAsset && <td>{l.product_name}</td>}
                      <td>${l.freight_rev.toFixed(2)}</td>
                      <td>${l.fsc_rev.toFixed(2)}</td>
                      {!isAsset && <td>${l.sand_rev.toFixed(2)}</td>}
                      <td>${l.total_rev.toFixed(2)}</td>
                      <td>${calcTotals(l).calc_rev.toFixed(2)}</td>
                      <td>${calcTotals(l).rev_diff.toFixed(2)}</td>
                      <td>{handleMismatch(calcTotals(l).rev_diff)}</td>
                    </tr>
                  ) : (
                    filter === "cost mismatch" &&
                    Math.abs(calcTotals(l).rev_diff) > mismatchValue && (
                      <tr key={idx}>
                        <td>{l.load_no}</td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.carrier_name}</td>
                      <td>{l.driver_name}</td>
                      {!isAsset && <td>{l.ticket_number}</td>}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      <td>{calcTotals(l).weight.toFixed(2)}</td>
                      <td>{calcTotals(l).weight_diff.toFixed(2)}</td>
                      {!isAsset && <td>{l.product_name}</td>}
                      <td>${l.freight_rev.toFixed(2)}</td>
                      <td>${l.fsc_rev.toFixed(2)}</td>
                      {!isAsset && <td>${l.sand_rev.toFixed(2)}</td>}
                      <td>${l.total_rev.toFixed(2)}</td>
                      <td>${calcTotals(l).calc_rev.toFixed(2)}</td>
                      <td>${calcTotals(l).rev_diff.toFixed(2)}</td>
                      <td>{handleMismatch(calcTotals(l).rev_diff)}</td>
                      </tr>
                    )
                  ),
                )}
              </tbody>
            </table>
          )}
        </div>
        {editAdjustmentTrigger && adjustments && (
          <AddAdjustment
            inv={invoice.invoice}
            setTrigger={setEditAdjustmentTrigger}
            projectId={currentProject.project.id}
            isVendor={false}
            id={adjustments.invoice_adjustment.id}
            editAdjustment={adjustments.invoice_adjustment}
            editAdjustmentFields={adjustments.adjustment_fields}
            setAdjustments={setAdjustments}
          />
        )}
        {deleteAdjustmentTrigger && (
          <DeleteAdjustment
            setTrigger={setDeleteAdjustmentTrigger}
            id={adjustments.invoice_adjustment.id}
            setAdjustments={setAdjustments}
            name={invoice.invoice.invoice_name}
          />
        )}
      </div>
    );
  }
}
